import _XCircleFilledIcon from "@adsk/alloy-react-icon/es/lib/XCircleFilledIcon";
import _MagnifyingGlassIcon from "@adsk/alloy-react-icon/es/lib/MagnifyingGlassIcon";
import _styled from "styled-components";
const _excluded = ["disabled", "loading", "style", "className", "defaultValue", "inlineText", "onClear", "onSearch"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import TextInput from '@adsk/alloy-react-text-input';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import useKeyDown from './hooks/useKeyDown';
/**
 * A SearchField component with a magnifying glass and a clear input text button
 */
const SearchField = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      disabled = false,
      loading = false,
      style,
      className,
      defaultValue,
      inlineText = '',
      onClear,
      onSearch
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const inputRef = useForwardedRef(ref);
  const [value, setValue] = useState(defaultValue || '');
  const onChange = e => {
    var _props$onChange;
    (_props$onChange = props.onChange) === null || _props$onChange === void 0 ? void 0 : _props$onChange.call(props, e.target.value);
    if (props.value === undefined) {
      setValue(e.target.value);
    }
  };
  const magnifyingGlassOnClick = () => {
    var _inputRef$current;
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    onSearch === null || onSearch === void 0 ? void 0 : onSearch();
  };
  const [rightSideWidth, setRightSideWidth] = useState(0);
  const rightSideWrapperRef = useRef(null);
  useEffect(() => {
    if (rightSideWrapperRef !== null && rightSideWrapperRef !== void 0 && rightSideWrapperRef.current) {
      var _rightSideWrapperRef$;
      const currentWidth = (_rightSideWrapperRef$ = rightSideWrapperRef.current) === null || _rightSideWrapperRef$ === void 0 ? void 0 : _rightSideWrapperRef$.getBoundingClientRect().width;
      setRightSideWidth(currentWidth);
    }
  }, [value, props.value, disabled, inlineText, loading]);
  const clearInput = () => {
    var _props$onChange2;
    (_props$onChange2 = props.onChange) === null || _props$onChange2 === void 0 ? void 0 : _props$onChange2.call(props, '');
    onClear === null || onClear === void 0 ? void 0 : onClear();
    if (props.value === undefined) {
      setValue('');
    }
  };
  useKeyDown(inputRef.current, 'Escape', clearInput);
  useKeyDown(inputRef.current, 'Enter', () => onSearch === null || onSearch === void 0 ? void 0 : onSearch());
  useKeyDown(inputRef.current, 'NumpadEnter', () => onSearch === null || onSearch === void 0 ? void 0 : onSearch());
  return /*#__PURE__*/React.createElement(_StyledDiv, {
    role: "searchbox",
    "aria-label": "search field",
    className: className,
    $_css: [{
      position: 'relative',
      width: '100%',
      height: 36
    }, style]
  }, /*#__PURE__*/React.createElement(_MagnifyingGlassIcon, {
    size: 20,
    role: "button",
    onClick: magnifyingGlassOnClick,
    style: _objectSpread(_objectSpread({
      color: theme.tokens.colors.icon.tertiary.value,
      left: 10,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 2,
      cursor: 'pointer'
    }, (props.value || value) && !disabled && {
      color: theme.tokens.colors.icon.secondary.value
    }), {}, {
      '&:hover': {
        color: theme.tokens.colors.icon.hover.value
      },
      '&:active': {
        color: theme.tokens.colors.icon.pressed.value
      }
    })
  }), /*#__PURE__*/React.createElement(TextInput, _extends({
    value: value
  }, props, {
    "data-testid": "SearchField__input",
    type: "search",
    ref: inputRef,
    disabled: disabled,
    onChange: onChange,
    style: _objectSpread(_objectSpread({}, disabled && {
      background: theme.tokens.colors.background.primary.value
    }), {}, {
      '& input[type="search"]': {
        paddingLeft: 36,
        paddingRight: rightSideWidth + theme.spacing.XS,
        paddingBottom: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      // Remove default browser clear button
      '& input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration': {
        display: 'none'
      }
    })
  })), /*#__PURE__*/React.createElement("div", {
    ref: rightSideWrapperRef,
    style: {
      position: 'absolute',
      height: 36,
      top: 0,
      right: 0,
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: 'flex-end',
      alignItems: 'center'
    }
  }, inlineText && /*#__PURE__*/React.createElement("span", {
    style: _objectSpread(_objectSpread({}, theme.typography.bodySmall), {}, {
      marginRight: theme.spacing.XS
    })
  }, inlineText), loading && /*#__PURE__*/React.createElement(ProgressRing, {
    "data-testid": "progress-ring",
    size: ProgressRing.SIZES.XSMALL,
    style: {
      marginRight: theme.spacing.XS
    }
  }), (props.value || value) && !disabled && /*#__PURE__*/React.createElement(_XCircleFilledIcon, {
    role: "button",
    size: 20,
    onClick: e => {
      e.preventDefault();
      clearInput();
    },
    style: [{
      color: theme.tokens.colors.icon.secondary.value,
      zIndex: 2,
      marginRight: theme.spacing.XS,
      cursor: 'pointer',
      '&:hover': {
        color: theme.tokens.colors.icon.hover.value
      },
      '&:active': {
        color: theme.tokens.colors.icon.secondary.value
      }
    }]
  })));
});
SearchField.displayName = 'SearchField';
SearchField.propTypes = {
  /* Styles applied to the input element */
  style: stylePropType,
  /* Class applied to the input element */
  className: PropTypes.string,
  /* Toggle disabled state */
  disabled: PropTypes.bool,
  /* Toggle loading spinner */
  loading: PropTypes.bool,
  /* initial default text value */
  defaultValue: PropTypes.string,
  /* controlled input value */
  value: PropTypes.string,
  /* Child input's onChange handler */
  onChange: PropTypes.func,
  /* Any text to display at the right of the input,
   * intended to show the number of results returned from the search
   **/
  inlineText: PropTypes.string
};
export default SearchField;
var _StyledDiv = _styled("div").withConfig({
  displayName: "SearchField___StyledDiv",
  componentId: "sc-k7hn6i-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=SearchField.js.map